import BackButton from 'components/Analytics/BackButton'
import AssetHeader from 'components/Analytics/Details/AssetHeader'
import DetailsChartsView from 'components/Analytics/Details/ChartsView'
import AnalyticsDetailsLinks from 'components/Analytics/Details/Links'
import PoolStatistics from 'components/Analytics/PoolDetails/PoolStatistics'
import { AssetHeaderSkeleton, ChartSkeleton, DetailsLinksSkeleton } from 'components/Analytics/Skeletons'
import TransactionsTable from 'components/Analytics/Table/TransactionsTable'
import { useAnalyticsTimeWindow } from 'hooks/analytics/useAnalyticsTimeWindow'
import { useV2ChartData } from 'hooks/analytics/useV2ChartData'
import { useV2PoolDatas } from 'hooks/analytics/useV2PoolDatas'
import { useV2PoolTransactions } from 'hooks/analytics/useV2PoolTransactions'
import { useV3ChartData } from 'hooks/analytics/useV3ChartData'
import { useV3PoolDatas } from 'hooks/analytics/useV3PoolDatas'
import { useV3PoolTransactions } from 'hooks/analytics/useV3PoolTransactions'
import { useAllTokens } from 'hooks/Tokens'
import useExplorerLinks from 'hooks/useExplorerLinks'
import { useGetTokenPrice } from 'hooks/useTokenPrice'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AnalyticsDetailsLink, AnalyticsVersion } from 'types/analytics'
import { buildAnalyticsPath } from 'utils/analytics'
import { getTokenLogoUri } from 'utils/tokenLogo'
import { Address } from 'viem'

export default function AnalyticsPoolPage() {
  const navigate = useNavigate()
  const { version: versionParam, poolId: poolIdParam } = useParams<{
    version?: string
    poolId?: string
  }>()
  const native = useNativeCurrency()

  const normalizedVersionParam = versionParam?.toLowerCase()
  const version: AnalyticsVersion = normalizedVersionParam === 'v2' ? 'v2' : 'v3'
  const poolId = poolIdParam ?? ''

  useEffect(() => {
    if (!poolId) return

    const isValidVersion = normalizedVersionParam === 'v2' || normalizedVersionParam === 'v3'

    if (!isValidVersion) {
      navigate(`/explore/v3/pools/${poolId}`, { replace: true })
    }
  }, [navigate, poolId, normalizedVersionParam])
  const poolIdArray = poolId ? [poolId] : undefined
  const { address: getExplorerLinkByAddress, token: getExplorerLinkByToken } = useExplorerLinks()

  const isV3Pool = version === 'v3'
  const isV2Pool = version === 'v2'
  const analyticsTimeWindow = useAnalyticsTimeWindow({
    enabled: Boolean(poolId),
  })

  const allTokens = useAllTokens()

  const v3PoolDatasQuery = useV3PoolDatas(poolIdArray, {
    enabled: isV3Pool && Boolean(poolId) && Boolean(analyticsTimeWindow.timestamps),
    timestamps: analyticsTimeWindow.timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
    blocksRequired: Boolean(analyticsTimeWindow.blocks),
  })

  const v2PoolDatasQuery = useV2PoolDatas(poolIdArray, {
    enabled: isV2Pool && Boolean(poolId) && Boolean(analyticsTimeWindow.timestamps),
    timestamps: analyticsTimeWindow.timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
    blocksRequired: Boolean(analyticsTimeWindow.blocks),
  })

  const currentPoolData = useMemo(
    () => (version === 'v3' ? v3PoolDatasQuery.data?.[0] : v2PoolDatasQuery.data?.[0]),

    [version, v3PoolDatasQuery.data, v2PoolDatasQuery.data]
  )

  const metrics24hLoaded = currentPoolData?.metrics24hLoaded ?? currentPoolData?.metricsLoaded ?? false

  const token0Address = currentPoolData?.token0?.address as Address | undefined
  const token1Address = currentPoolData?.token1?.address as Address | undefined

  const { data: v3ChartData, isLoading: isV3ChartLoading } = useV3ChartData(0, 0, {
    poolAddress: poolId,
    enabled: isV3Pool && Boolean(poolId),
  })

  const { data: v2ChartData, isLoading: isV2ChartLoading } = useV2ChartData({
    enabled: isV2Pool && Boolean(poolId),
    pairAddress: poolId,
  })

  const usingChartData = useMemo(() => {
    if (version === 'v3') return v3ChartData

    return v2ChartData
  }, [version, v2ChartData, v3ChartData])

  const token0Price = useGetTokenPrice(token0Address ?? native.wrapped.address, {
    enabled: Boolean(token0Address),
  })
  const token1Price = useGetTokenPrice(token1Address ?? native.wrapped.address, {
    enabled: Boolean(token1Address),
  })

  const poolNameLinks = useMemo(() => {
    if (!currentPoolData) return []

    return [
      currentPoolData.token0?.address && currentPoolData.token0?.symbol
        ? {
            label: currentPoolData.token0.symbol,
            href: `/explore/${version}/tokens/${currentPoolData.token0.address}`,
          }
        : null,
      currentPoolData.token1?.address && currentPoolData.token1?.symbol
        ? {
            label: currentPoolData.token1.symbol,
            href: `/explore/${version}/tokens/${currentPoolData.token1.address}`,
          }
        : null,
    ].filter((token): token is { label: string; href: string } => Boolean(token?.href) && Boolean(token?.label))
  }, [currentPoolData, version])

  const token0Logo = useMemo(
    () => getTokenLogoUri(Object.values(allTokens), currentPoolData?.token0?.address),
    [allTokens, currentPoolData]
  )
  const token1Logo = useMemo(
    () => getTokenLogoUri(Object.values(allTokens), currentPoolData?.token1?.address),
    [allTokens, currentPoolData]
  )

  const v3PoolTransactionsQuery = useV3PoolTransactions(poolId, {
    enabled: isV3Pool && Boolean(poolId),
  })

  const v2PoolTransactionsQuery = useV2PoolTransactions(poolId, {
    enabled: isV2Pool && Boolean(poolId),
  })

  const poolTransactions = useMemo(
    () => (isV3Pool ? v3PoolTransactionsQuery.data : v2PoolTransactionsQuery.data),
    [isV3Pool, v2PoolTransactionsQuery.data, v3PoolTransactionsQuery.data]
  )

  const poolLinks: AnalyticsDetailsLink[] = useMemo(() => {
    if (!currentPoolData) return []
    return [
      {
        name: `${currentPoolData.token0.symbol} / ${currentPoolData.token1.symbol}`,
        address: currentPoolData.address,
        imageURI: [token0Logo, token1Logo],
        href: getExplorerLinkByAddress(currentPoolData.address) ?? '',
      },
      {
        name: currentPoolData.token0?.symbol,
        address: currentPoolData.token0?.address,
        imageURI: token0Logo,
        href: getExplorerLinkByToken(currentPoolData.token0.address) ?? '',
      },
      {
        name: currentPoolData.token1.symbol,
        address: currentPoolData.token1.address,
        imageURI: token1Logo,
        href: getExplorerLinkByToken(currentPoolData?.token1.address) ?? '',
      },
    ]
  }, [currentPoolData, getExplorerLinkByAddress, getExplorerLinkByToken, token0Logo, token1Logo])

  const liquidityChartConfig = useMemo(() => {
    if (!isV3Pool) return undefined
    const token0Meta = currentPoolData?.token0
    const token1Meta = currentPoolData?.token1
    return {
      enabled: Boolean(currentPoolData),
      token0: {
        address: token0Meta?.address,
        symbol: token0Meta?.symbol,
        name: token0Meta?.name,
        decimals: token0Meta?.decimals,
      },
      token1: {
        address: token1Meta?.address,
        symbol: token1Meta?.symbol,
        name: token1Meta?.name,
        decimals: token1Meta?.decimals,
      },
      feeTier: currentPoolData?.feeTier,
      currentPrice: currentPoolData?.token0Price,
    }
  }, [currentPoolData, isV3Pool])

  const fallbackBackButtonURI = useMemo(() => buildAnalyticsPath(version, 'pools'), [version])

  const isPoolDataLoading = isV3Pool ? v3PoolDatasQuery.isLoading : v2PoolDatasQuery.isLoading
  const isChartLoading = isV3Pool ? isV3ChartLoading : isV2ChartLoading
  const isTransactionsLoading = isV3Pool ? v3PoolTransactionsQuery.isLoading : v2PoolTransactionsQuery.isLoading

  const showAssetSkeleton = isPoolDataLoading || !currentPoolData
  const showLinksSkeleton = isPoolDataLoading || !currentPoolData

  const renderPoolStatistics = () => (
    <PoolStatistics
      version={version}
      token0={{
        address: currentPoolData?.token0?.address ?? '',
        symbol: currentPoolData?.token0?.symbol,
        balance: currentPoolData?.tvlToken0,
        price: token0Price.price ?? undefined,
      }}
      token1={{
        address: currentPoolData?.token1?.address ?? '',
        symbol: currentPoolData?.token1?.symbol,
        balance: currentPoolData?.tvlToken1,
        price: token1Price.price ?? undefined,
      }}
      feeTier={currentPoolData?.feeTier ?? 0}
      tvl={{
        value: currentPoolData?.tvlUSD,
        percentageChange: metrics24hLoaded ? currentPoolData?.tvlUSDChange : undefined,
      }}
      volume={{
        value: metrics24hLoaded ? currentPoolData?.volumeUSD : undefined,
        percentageChange: metrics24hLoaded ? currentPoolData?.volumeUSDChange : undefined,
      }}
      fees={{
        value: metrics24hLoaded ? currentPoolData?.feesUSD : undefined,
      }}
      APR={currentPoolData?.APR}
    />
  )

  const renderPoolLinks = () =>
    showLinksSkeleton ? <DetailsLinksSkeleton /> : <AnalyticsDetailsLinks links={poolLinks} />

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <BackButton fallbackURI={fallbackBackButtonURI} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-15">
        <div className="lg:col-span-2 flex flex-col gap-9">
          {showAssetSkeleton ? (
            <AssetHeaderSkeleton />
          ) : (
            <AssetHeader
              logoURI={[token0Logo, token1Logo]}
              name={`${currentPoolData?.token0.symbol} / ${currentPoolData?.token1.symbol}`}
              nameLinks={poolNameLinks}
              version={version}
              feeTier={`${(currentPoolData?.feeTier ?? 0) / 10_000}%`}
              links={[getExplorerLinkByAddress(poolId) ?? '']}
            />
          )}

          <div className="w-full flex justify-center items-center">
            {isChartLoading ? (
              <ChartSkeleton className="w-full min-h-[428px]" hasManageBar manageBarItems={4} />
            ) : (
              <DetailsChartsView
                view="pools"
                data={usingChartData}
                poolId={poolId}
                initialChartView="volume"
                liquidityChartConfig={liquidityChartConfig}
              />
            )}
          </div>

          <div className="flex flex-col gap-4 md:hidden">
            {renderPoolStatistics()}
            {renderPoolLinks()}
          </div>

          <TransactionsTable transactions={poolTransactions ?? []} isLoading={isTransactionsLoading} />
        </div>

        <aside className="hidden flex-col gap-6 lg:col-span-1 pb-20 md:flex">
          {renderPoolStatistics()}

          {renderPoolLinks()}
        </aside>
      </div>
    </div>
  )
}
